import AdminPage from '../../components/admin/AdminPage';
import { useKycDetail } from './hooks/useKycDetail';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  CardSubtitle,
  Row,
  Col,
  Table,
  Button,
  Alert,
} from 'reactstrap';
import LoadingCard from '../../components/LoadingCard';
import KycStatusBadge from './components/KycStatusBadge';
import { KycStatus } from './constants/kycConstants';
import ResendKycModal from './components/ResendKycModal';
import RenderQuestion from './components/RenderQuestion';
import useModalState from '../../hooks/useModalState';
import CustomerReviewModal from './components/CustomerReviewModal';
import React, { useState } from 'react';
import KycStatusLogModal from './components/KycStatusLogModal';
import KycComplianceModal from './components/KycComplianceModal';

const KycDetail = () => {
  const { id = '' } = useParams();
  const [showCustomerReviewModal, toggleCustomerReviewModal] = useModalState();
  const [showStatusLogModal, setShowStatusLogModal] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const pageName = 'Detalle de formulario KYC';
  const pageParentName = 'Cumplimiento';
  const {
    kycDetail,
    errorDetail,
    isLoading,
    resendModalOpen,
    setResendModalOpen,
    handleChangeStatus,
    handleForceMovePhase,
    handleSendMail,
    handleGenerateDocument,
    handleSendWhatsapp,
    handleCustomerReviewForm,
    //Test error alert
    defaultEmptyAlert,
    alertProps,
    setAlertProps,
    clearAlert,
  } = useKycDetail(id);

  // Validación para mostrar el botón "Reenviar"
  const showResendButton =
    kycDetail?.kyc?.status === KycStatus.CREATED ||
    kycDetail?.kyc?.status === KycStatus.IN_PROGRESS ||
    kycDetail?.kyc?.status === KycStatus.IN_REVIEW ||
    kycDetail?.kyc?.status === KycStatus.NEED_CUSTOMER_REVIEW;

  const showDisabledRejectButton =
    kycDetail?.kyc?.status === KycStatus.CREATED ||
    kycDetail?.kyc?.status === KycStatus.IN_PROGRESS ||
    kycDetail?.kyc?.status === KycStatus.IN_REVIEW ||
    kycDetail?.kyc?.status === KycStatus.NEED_CUSTOMER_REVIEW;

  if (isLoading || !kycDetail) {
    return <LoadingCard name={pageName} parent_name={pageParentName} />;
  }

  return (
    <AdminPage
      name={pageName}
      parentName={pageParentName}
      globalAlertOptions={alertProps}
      footer={
        <div className="d-flex justify-content-end gap-2">
          {showResendButton && (
            <Button color="primary" onClick={() => setResendModalOpen(true)}>
              Reenviar
            </Button>
          )}
          {showDisabledRejectButton && (
            <>
              <Button
                color="warning"
                // onClick={() => handleChangeStatus(KycStatus.DISABLED)}
                onClick={() =>
                  setAlertProps((state) => ({
                    ...state,
                    warning: true,
                    title: 'Deshabilitar KYC',
                    text: 'Esta acción deshabilitará el KYC y no podrá ser editado. ¿Desea continuar?',
                    onConfirm: () => {
                      handleChangeStatus(KycStatus.DISABLED);
                      clearAlert();
                    },
                    onClickOutside: () => {
                      clearAlert();
                    },
                    onCancel: () => {
                      clearAlert();
                    },
                  }))
                }
              >
                Deshabilitar
              </Button>
            </>
          )}
          {kycDetail.kyc.status === KycStatus.IN_REVIEW && (
            <>
              <Button
                color="danger"
                onClick={
                  // handleChangeStatus(KycStatus.NEED_CUSTOMER_REVIEW);
                  toggleCustomerReviewModal
                }
              >
                Pedir revisión al cliente
              </Button>
              <Button
                color="success"
                onClick={() => handleChangeStatus(KycStatus.COMPLETED)}
              >
                Marcar como completado
              </Button>
            </>
          )}
          {kycDetail.kyc.status === KycStatus.PARTIALLY_COMPLETED && (
            <Button
              color="warning"
              onClick={() =>
                setAlertProps((state) => ({
                  ...state,
                  warning: true,
                  title: 'Mover KYC de fase 1 a fase 2',
                  text: 'Esta acción moverá el KYC de fase 1 a fase 2 de manera forzada, usar esta acción unicamente si el pago fue registrado con anterioridad. ¿Desea continuar?',
                  onConfirm: () => {
                    handleForceMovePhase();
                    clearAlert();
                  },
                  onClickOutside: () => {
                    clearAlert();
                  },
                  onCancel: () => {
                    clearAlert();
                  },
                }))
              }
            >
              Forzar mover a fase 2
            </Button>
          )}
        </div>
      }
    >
      <h3 className="mb-4" style={{ fontSize: '1.5rem', fontWeight: 600 }}>
        Detalle del formulario KYC
      </h3>
      {isLoading && (
        <p style={{ fontSize: '1rem', fontWeight: 400 }}>Cargando detalle...</p>
      )}
      {errorDetail && (
        <p
          className="text-danger"
          style={{ fontSize: '1rem', fontWeight: 400 }}
        >
          {errorDetail}
        </p>
      )}

      {/* KYC Node Summary */}
      {kycDetail?.kyc && (
        <Card className="mb-4">
          <CardBody>
            <CardTitle
              tag="h5"
              style={{ fontSize: '1.25rem', fontWeight: 600 }}
            >
              Información general
            </CardTitle>
            <Row className="mb-2" style={{ fontSize: '1rem', fontWeight: 400 }}>
              <Col md={6}>
                <strong>Proyecto:</strong> {kycDetail.kyc.project_name}
              </Col>
              <Col md={6}>
                <strong>Unidad:</strong> {kycDetail.kyc.unit_name}
              </Col>
            </Row>
            <Row className="mb-2" style={{ fontSize: '1rem', fontWeight: 400 }}>
              <Col md={6} className="d-flex align-items-center">
                <strong>Invitado:</strong> {kycDetail.kyc.guest?.name}
                {kycDetail.kyc.guest?.lists &&
                  kycDetail.kyc.guest.lists.length > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="ms-3"
                      style={{
                        color: '#dc3545', // Bootstrap red
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: 500,
                        outline: 'none',
                        userSelect: 'none',
                        textDecoration: 'underline',
                        paddingLeft: '0.5rem',
                      }}
                      onClick={() => setShowComplianceModal(true)}
                    >
                      Ver listas de cumplimiento
                    </span>
                  )}
              </Col>
              <Col md={6}>
                <strong>Email:</strong> {kycDetail.kyc.guest?.email}
              </Col>
            </Row>
            <Row className="mb-2" style={{ fontSize: '1rem', fontWeight: 400 }}>
              <Col md={6}>
                <strong>Teléfono:</strong> {kycDetail.kyc.guest?.phone}
              </Col>
              <Col md={6} className="d-flex align-items-center">
                <strong>Estado:</strong>{' '}
                <KycStatusBadge status={kycDetail.kyc.status} />
                <span
                  role="button"
                  tabIndex={0}
                  className="ms-3"
                  style={{
                    color: '#0d6efd',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 500,
                    outline: 'none',
                    userSelect: 'none',
                    textDecoration: 'underline',
                    paddingLeft: '0.5rem',
                  }}
                  onClick={() => setShowStatusLogModal(true)}
                >
                  Ver historial
                </span>
              </Col>
            </Row>
            <Row className="mb-2" style={{ fontSize: '1rem', fontWeight: 400 }}>
              <Col md={6}>
                <strong>Última actualización:</strong>{' '}
                {kycDetail.kyc.updated_at}
              </Col>
              <Col md={6}>
                <strong>Creado:</strong> {kycDetail.created_at}
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                {kycDetail.kyc.status === KycStatus.COMPLETED &&
                kycDetail.kyc.document_file ? (
                  <a
                    href={kycDetail.kyc.document_file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-success btn-sm"
                    style={{ fontWeight: 500 }}
                  >
                    Ver documento KYC
                  </a>
                ) : kycDetail.kyc.status === KycStatus.COMPLETED &&
                  !kycDetail.kyc.document_file ? (
                  <Button
                    color="warning"
                    outline
                    size="sm"
                    style={{ fontWeight: 500 }}
                    onClick={() =>
                      setAlertProps((state) => ({
                        ...state,
                        onConfirm: () => handleGenerateDocument(),
                        onCancel: () => setAlertProps(defaultEmptyAlert),
                        warning: true,
                        success: false,
                        danger: false,
                        title: 'Generación de Documento KYC',
                        text: '¿Estás seguro de que deseas generar el documento KYC?. Este proceso puede tardar unos minutos.',
                      }))
                    }
                  >
                    Generar documento KYC
                  </Button>
                ) : null}
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      {kycDetail?.kyc?.has_errors && kycDetail?.kyc?.comments && (
        <Card
          className="mb-4"
          style={{
            border: '1.5px solid #dc3545', // Bootstrap red
            background: '#fff6f6',
          }}
        >
          <CardBody>
            <div
              style={{
                color: '#dc3545',
                fontSize: '1rem',
                whiteSpace: 'pre-line',
              }}
            >
              <span style={{ fontWeight: 600 }}>
                Formulario posee observaciones
              </span>
              <br />
              <span style={{ fontWeight: 600 }}>Comentario:</span>{' '}
              {kycDetail.kyc.comments}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Steps */}
      {kycDetail?.steps?.map((step: any, idx: any) => (
        <Card key={idx} className="mb-4">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <CardTitle
                tag="h5"
                style={{ fontSize: '1.15rem', fontWeight: 600 }}
              >
                {step.title}
              </CardTitle>
              {/* Usa KycStatusBadge para mostrar el estado del step */}
              {step.status && <KycStatusBadge status={step.status} />}
            </div>
            <CardSubtitle
              className="mb-3"
              tag="h6"
              style={{ fontSize: '1rem', fontWeight: 500 }}
            >
              {step.subtitle}
            </CardSubtitle>
            <Table bordered>
              <tbody>
                {step.questions.map((question: any) => (
                  <RenderQuestion key={question.name} question={question} />
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      ))}
      <ResendKycModal
        isOpen={resendModalOpen}
        onClose={() => setResendModalOpen(false)}
        handleSendMail={handleSendMail}
        handleSendWhatsapp={handleSendWhatsapp}
        guestEmail={kycDetail?.kyc?.guest?.email}
        guestPhone={kycDetail?.kyc?.guest?.phone}
      />
      <CustomerReviewModal
        isOpen={showCustomerReviewModal}
        onClose={toggleCustomerReviewModal}
        onSubmit={handleCustomerReviewForm}
      />
      <KycStatusLogModal
        isOpen={showStatusLogModal}
        onClose={() => setShowStatusLogModal(false)}
        logs={kycDetail?.status_log || []}
      />
      <KycComplianceModal
        isOpen={showComplianceModal}
        onClose={() => setShowComplianceModal(false)}
        lists={kycDetail?.kyc?.guest?.lists || []}
      />
    </AdminPage>
  );
};

export default KycDetail;
